import { useMentions } from '@/store/mentions/hooks';
import { useUserId } from '@/hooks/auth';
import { ModalWrapper } from '../base/ModalWrapper';
import { useCallback, useMemo, useState } from 'react';
import { DashboardCard } from '../base/DashboardCard';
import MentionCard from './MentionCard';
import EmptyMessageCard from '../base/EmptyMessageCard';
import type { Mention } from '@/store/mentions/types';
import { useDrawerActions } from '@/store/drawer';
import type { Person } from '@/store/people/types';
import { loadPerson } from '@/store/people/actions/people';
import { usePeopleStore } from '@/store/people';
import { useApiWithAuth } from '@/hooks/api';
import { Empty } from '@/components/common/empty';

export function RecentMentions() {
  const userId = useUserId() || '';
  const limit = 5;
  const { loading, recent, active } = useMentions(userId);
  const [isViewAllOpen, setIsViewAllOpen] = useState(false);
  const {
    setOpen,
    setCurrentPerson,
    setCurrentTab,
    setCurrentComment,
    setCurrentMention,
  } = useDrawerActions();
  const peopleStore = usePeopleStore();
  const { api, auth } = useApiWithAuth();
  const sublist = useMemo(() => recent.slice(0, limit), [recent, limit]);

  const handleMentionClick = useCallback(
    (mention: Mention) => {
      return loadPerson(peopleStore, {
        api,
        auth,
        id: mention.data.profile_id,
        force: true,
        cache: false,
      }).then((result) => {
        setOpen(true);

        if (!result.exists) {
          console.warn('Person does not exist');
        } else if (result.exists && result.loaded) {
          setCurrentPerson(result.item as Person);
          setCurrentComment(mention.data.comment_id);
          setCurrentMention(mention.id);
          setCurrentTab('comments');
        }

        return;
      });
    },
    [
      setOpen,
      setCurrentPerson,
      setCurrentTab,
      setCurrentComment,
      setCurrentMention,
      api,
      auth,
      peopleStore,
    ],
  );

  return (
    <>
      <DashboardCard loading={loading}>
        <DashboardCard.Header
          title='Mentions'
          subtitle='your recent mentions'
          count={active.length}
          onViewAll={() => setIsViewAllOpen(true)}
        />
        <DashboardCard.Body
          empty={active.length === 0}
          emptyContent={
            <EmptyMessageCard
              icon='mdi:comment-outline'
              message="You're all caught up!"
            />
          }
        >
          {sublist.map((mention) => (
            <div key={mention.id} onClick={() => handleMentionClick(mention)}>
              <MentionCard data={mention.data} />
            </div>
          ))}
        </DashboardCard.Body>
      </DashboardCard>

      {/* Modal for viewing all mentions */}
      <ModalWrapper
        isOpen={isViewAllOpen}
        onClose={() => setIsViewAllOpen(false)}
        title='All Mentions'
        size='lg'
      >
        <>
          {!active.length && (
            <Empty
              description="You're all caught up!"
              icon='mdi:comment-outline'
            />
          )}
          {Boolean(active.length) && (
            <div className='space-y-2'>
              {active.map((mention) => (
                <div
                  key={mention.id}
                  onClick={() => handleMentionClick(mention)}
                >
                  <MentionCard data={mention.data} />
                </div>
              ))}
            </div>
          )}
        </>
      </ModalWrapper>
    </>
  );
}
